/**
 * ISS-039 — 감사 로그 CSV export 훅.
 *
 * 흐름:
 *  1) POST /export-csv → 413 시 한도 초과 모달 메시지 set + return
 *                     → 400 시 토스트 (날짜 범위 오류)
 *                     → 200 시 job_id 보관 + 다음 단계
 *  2) GET /download/{id} blob (responseType: 'blob')
 *  3) URL.createObjectURL + 임시 <a download> 클릭 + revokeObjectURL
 *
 * 폴링 없음 (백엔드가 streaming 이라 즉시 응답 시작).
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import client from '../api/client';
import * as auditApi from '../api/audit';
import { toast } from '../components/common/Toast';

interface UseAuditCsvExportResult {
  exporting: boolean;
  // 한도 초과 (413) 시 모달에 표시할 메시지. null 이면 모달 미노출
  limitExceededMessage: string | null;
  clearLimitExceededMessage: () => void;
  start: (data: { date_from: string; date_to: string; action?: string }) => Promise<void>;
}

const DOWNLOAD_FILENAME_FALLBACK = 'audit_log.csv';

const extractFilename = (contentDisposition: string | undefined): string => {
  if (!contentDisposition) return DOWNLOAD_FILENAME_FALLBACK;
  // RFC 5987 우선: filename*=UTF-8''<encoded>
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      /* fallthrough */
    }
  }
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : DOWNLOAD_FILENAME_FALLBACK;
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

export const useAuditCsvExport = (): UseAuditCsvExportResult => {
  const [exporting, setExporting] = useState(false);
  const [limitExceededMessage, setLimitExceededMessage] = useState<string | null>(null);

  const clearLimitExceededMessage = useCallback(() => {
    setLimitExceededMessage(null);
  }, []);

  const start = useCallback(async (data: { date_from: string; date_to: string; action?: string }) => {
    setExporting(true);
    setLimitExceededMessage(null);

    let exportRes: auditApi.CsvExportResponse;
    // Step 1: POST /export-csv
    try {
      exportRes = await auditApi.exportCsv(data);
      toast.success(exportRes.message);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const errorMsg =
          (err.response?.data as { error?: string } | undefined)?.error ||
          'CSV 내보내기 실패';
        if (status === 413) {
          setLimitExceededMessage(errorMsg);
        } else {
          toast.error(errorMsg);
        }
      } else {
        toast.error('CSV 내보내기 실패');
      }
      setExporting(false);
      return;
    }

    // Step 2 & 3: GET /download → blob → <a> trigger.
    // 헤더 추출이 필요해 axios client 직접 사용.
    try {
      const res = await client.get(`/api/v1/audit/download/${exportRes.job_id}`, {
        responseType: 'blob',
      });
      const blob = res.data as Blob;
      const cd = (res.headers['content-disposition'] as string | undefined);
      const filename = extractFilename(cd);
      triggerBlobDownload(blob, filename);
    } catch (err) {
      // F3 — Blob → JSON 변환 (catch 블록)
      if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text) as { error?: string };
          toast.error(json.error || '다운로드 실패');
        } catch {
          toast.error('다운로드 실패');
        }
      } else {
        const msg = err instanceof Error ? err.message : '다운로드 실패';
        toast.error(msg);
      }
    } finally {
      setExporting(false);
    }
  }, []);

  return { exporting, limitExceededMessage, clearLimitExceededMessage, start };
};
